import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

const DISMISSED_VERSION_KEY = 'autoUpdate.dismissedVersion';
const LAST_PROMPTED_AT_KEY = 'autoUpdate.lastPromptedAt';
const REMIND_INTERVAL_MS = 6 * 60 * 60 * 1000;
const REMIND_CHECK_INTERVAL_MS = 5 * 60 * 1000;

interface DownloadProgress {
  bytesPerSecond: number;
  percent: number;
  transferred: number;
  total: number;
}

type UpdateStatus =
  | { status: 'idle' | 'checking' }
  | { status: 'available'; version: string; releaseNotes?: string; mandatory: boolean }
  | { status: 'downloading'; version: string; releaseNotes?: string; mandatory: boolean }
  | { status: 'downloaded'; version: string; releaseNotes?: string; mandatory: boolean }
  | { status: 'error'; message: string };

type DownloadedStatus = Extract<UpdateStatus, { status: 'downloaded' }>;

interface AutoUpdateContextValue {
  status: UpdateStatus;
  downloadProgress: DownloadProgress | null;
  popupOpen: boolean;
  reminderVisible: boolean;
  handleRemindLater: () => void;
  handleRestartNow: () => Promise<void>;
}

const AutoUpdateContext = createContext<AutoUpdateContextValue | undefined>(undefined);

export function useAutoUpdate(): AutoUpdateContextValue {
  const ctx = useContext(AutoUpdateContext);
  if (!ctx) {
    throw new Error('useAutoUpdate must be used within AutoUpdateProvider');
  }
  return ctx;
}

export function AutoUpdateProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<UpdateStatus>({ status: 'idle' });
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress | null>(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [reminderVisible, setReminderVisible] = useState(false);

  const statusRef = useRef(status);
  statusRef.current = status;
  const handledVersionRef = useRef<string | null>(null);

  const isDownloadedOptional = status.status === 'downloaded' && !status.mandatory;

  const openPopupFor = useCallback(() => {
    setPopupOpen(true);
    setReminderVisible(false);
    if (typeof window !== 'undefined') {
      localStorage.setItem(LAST_PROMPTED_AT_KEY, String(Date.now()));
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.electron?.isElectron) return;
    const electron = window.electron;

    const hydrate = async () => {
      try {
        const pending = await electron.getPendingUpdate?.();
        if (pending && pending.version && !pending.mandatory) {
          setStatus({
            status: 'downloaded',
            version: pending.version,
            releaseNotes: pending.releaseNotes,
            mandatory: false,
          });
        }
      } catch (error) {
        console.error('Failed to load pending update:', error);
      }
    };
    hydrate();

    const offStatus = electron.onUpdateStatus?.((data: UpdateStatus) => {
      setStatus(data);
    });
    const offProgress = electron.onDownloadProgress?.((p: DownloadProgress) => {
      setDownloadProgress(p);
    });

    return () => {
      offStatus?.();
      offProgress?.();
    };
  }, []);
  useEffect(() => {
    if (!isDownloadedOptional) return;
    const version = (status as DownloadedStatus).version;
    if (handledVersionRef.current === version) return;
    handledVersionRef.current = version;

    const dismissedVersion = localStorage.getItem(DISMISSED_VERSION_KEY);
    if (dismissedVersion !== version) {
      openPopupFor();
    } else {
      setReminderVisible(true);
    }
  }, [isDownloadedOptional, status, openPopupFor]);

  // Re-surface the popup every 6 hours while a downloaded update sits
  // dismissed and un-installed.
  useEffect(() => {
    const interval = setInterval(() => {
      const current = statusRef.current;
      if (current.status !== 'downloaded' || current.mandatory) return;

      const dismissedVersion = localStorage.getItem(DISMISSED_VERSION_KEY);
      if (dismissedVersion !== current.version) return;

      const lastPromptedAt = Number(localStorage.getItem(LAST_PROMPTED_AT_KEY) || 0);
      if (Date.now() - lastPromptedAt >= REMIND_INTERVAL_MS) {
        openPopupFor();
      }
    }, REMIND_CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [openPopupFor]);

  const handleRemindLater = useCallback(() => {
    if (status.status === 'downloaded') {
      localStorage.setItem(DISMISSED_VERSION_KEY, status.version);
      localStorage.setItem(LAST_PROMPTED_AT_KEY, String(Date.now()));
    }
    setPopupOpen(false);
    setReminderVisible(true);
  }, [status]);

  const handleRestartNow = useCallback(async () => {
    try {
      localStorage.removeItem(DISMISSED_VERSION_KEY);
      localStorage.removeItem(LAST_PROMPTED_AT_KEY);
      if (typeof window !== 'undefined' && window.electron?.restartApp) {
        await window.electron.restartApp();
      }
    } catch (error) {
      console.error('Failed to restart app:', error);
    }
  }, []);

  return (
    <AutoUpdateContext.Provider
      value={{
        status,
        downloadProgress,
        popupOpen,
        reminderVisible,
        handleRemindLater,
        handleRestartNow,
      }}
    >
      {children}
    </AutoUpdateContext.Provider>
  );
}
